import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile, isMobileOnly } from 'react-device-detect';
import styled, { css } from 'styled-components';
import { isEmpty, find } from 'lodash-es';
import moment from 'moment';
import {
  BaseBox,
  FlexBox,
  Heading4,
  Body1,
  Body2,
} from '@docon/docon-ui-library';
import { getAppointments } from 'redux/reducer/appointment';
import Layout, { MainBody } from 'components/Layout';
import AppointmentCard from 'components/Appointment/components/Card';
import { getTimeFromMinutes } from 'utils';
import { Loader, Image } from 'shared';
import { downloadApp } from 'shared/DownloadApp';
import { cleverTapAnl } from 'utilities';
import { bgColor, borderColor, colors } from 'utilities/colorConfig';

const NeedHelpPic = '/images/needhelp.svg';

const Heading = styled(Heading4)`
  padding: 18px 26px 16px;
  font-weight: 600;
`;

const AppBanner = styled(FlexBox)`
  position: sticky;
  z-index: 1;
  padding: 14px 26px;
  gap: 20px;
  background: ${colors.blueColor[3]};
  box-shadow: 0px 4px 12px rgba(0, 41, 77, 0.25);
  border-radius: 14px;
`;

const VcText = styled(Body1)`
  font-weight: 600;
  flex: 1;
  color: ${colors.primaryColor};
`;

const Download = styled(Body2)`
  padding: 2px 5px;
  align-self: flex-end;
  border-radius: 5px;
  color: ${colors.white};
  background-color: ${bgColor.success};
`;

const TabContent = styled(BaseBox)`
  margin: 36px 26px 0px 26px;
  text-align: center;

  ${Image} {
    width: 35vh;
    height: 35vh;
  }
`;

const ListItem = styled(Body1)`
  padding: 10px 26px;
  font-weight: 500;
  color: ${colors.primaryBlue};
  background-color: ${colors.white};
  cursor: pointer;

  & + & {
    border-top: 1px solid ${borderColor};
  }
`;

const Root = styled(Layout)`
  ${() => isMobileOnly && css`
    ${MainBody} { padding: 0; }
  `}
`;

const otherQueries = [
  {
    title: 'FAQs',
    key: 'faq',
  },
  {
    title: 'Legal Terms & Conditions',
    key: 'ltc',
  },
];

const HelpContent = 'It looks like you are experiencing some problem with us. We are here to help you, so please get in touch with us.';

function VcSupportComponent({
  history,
}: any) {
  const { user } = useSelector(state => state.auth);
  const {
    appointments, isFetchingAppointments,
  } = useSelector(state => state.appointment);
  const dispatch = useDispatch();

  function fetchAppointments() {
    if (!user.userID || !user.mobileNumber) return;

    dispatch(getAppointments({
      userID: user.userID, params: {
        mobileNumber: user.mobileNumber,
        offset: 20,
        teleConsult: true,
        type: 'appointment',
      },
    }));
  }

  function triggerAppointmentSelectEvent(appointment) {
    cleverTapAnl.event({
      event_name: 'help_appointment_select',
      event: {
        page_name: 'help',
        appointment_status: appointment.status,
        appointment_id: appointment.appointmentID,
        teleconsult_id: appointment.teleConsultSessionObject?.id,
        doctor_id: appointment.doctorID,
        appointment_date: moment(appointment.appointmentDisplayTime).format('YYYY-MM-DD'),
        slot_type: appointment.isTeleConsult ? 'video' : 'physical',
        slot_time: getTimeFromMinutes(appointment.appointmentTime),
        transaction_amount: appointment.teleConsultSessionObject?.pricing_details.total,
      },
    });
  }

  function handleCardClick(appointment) {
    triggerAppointmentSelectEvent(appointment);
    history.push(`/support/appointment/${appointment.appointmentID}`);
  }

  function handleItemClick(event: any) {
    const key = event?.currentTarget?.id;

    switch (key) {
      case 'otherAppointments':
        history.push('/support/appointment');
        break;
      case 'faq':
        history.push('/support/help');
        break;
      case 'ltc':
        history.push('/support/privacy');
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    fetchAppointments();
  }, [user]);

  function renderContent() {
    const recentAppointment = find(appointments, 'isTeleConsult') || {};
    return (
      <>
        <AppBanner hidden={!isMobile}>
          <VcText>
            <mark style={{ color: colors.error, background: 'none' }}>
              Video consultation
            </mark> is available on docon app
          </VcText>
          <Download
            title={'Download'}
            onClick={downloadApp}
          />
        </AppBanner>

        {
          !isEmpty(recentAppointment) ? (
            <>
              <Heading title={'My recent appointments'} />
              <AppointmentCard
                mx={isMobileOnly ? '16px' : 0}
                mb={'25px'}
                appointment={recentAppointment}
                onCardClick={handleCardClick}
              />

              <ListItem
                id='otherAppointments'
                title={'Issue with other appointments'}
                onClick={handleItemClick}
              />
            </>
          ) :
            <TabContent>
              <Heading title={'Need some help?'} />
              <Body1 color={colors.secondaryColor} title={HelpContent} />
              <Image src={NeedHelpPic} />
            </TabContent>
        }

        <Heading title={'Help with other queries'} />
        {
          otherQueries.map(query => (
            <ListItem
              id={query.key}
              key={query.key}
              title={query.title}
              onClick={handleItemClick}
            />
          ))
        }
      </>
    );
  }

  return (
    <Root>
      {
        isFetchingAppointments ? <Loader /> : renderContent()
      }
    </Root>
  );
}

export default VcSupportComponent;